/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */

/**
 *
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @version 1.12.0
 * @since 1.0.1
 */

import { FC, useCallback } from 'react';

import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';


import { icons } from '../../../../../../assets';
import { LinkBlock } from '../../../../../../components/common';
import { FlexColumnStyled, FlexStyled } from '../../../../../../components/common/Block/Flexbox';
import { ImgStyled } from '../../../../../../components/common/Block/Img';
import { LayoutStyled } from '../../../../../../components/common/Block/Layout';
import { ELinkTarget } from '../../../../../../components/common/ExternalLink/ExternalLink';
import ArrowTooltip from '../../../../../../components/common/Tooltip/Tooltip';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { IWidgetPersonalAccountSuccessTranslation } from '../../../../../../modules/internationalization/types/pages/dashboard/widgetPersonalAccount';
import { openExternalRoute } from '../../../../../../routes/constants/externalRoutes';
import { RoutePath } from '../../../../../../routes/constants/routesConstants';
import declination from '../../../../../../utils/declination';
import { formatBalanceWithoutTrailingZeros } from '../../../../../../utils/formatText';
import { useCheckCustomRole } from '../../../../../../utils/useCheckCustomRole';
import {
    WidgetPersonalAccountCashbackRowBodyStyled,
    WidgetPersonalAccountCashbackTitleStyled,
} from '../styled/WidgetPersonalAccountStyled';
import { IWidgetBalance, IWidgetPersonalAccountCashbackUrl } from '../types/widgetPersonalAccountType';

export interface IWidgetPersonalAccountCashbackProps {
    balance: IWidgetBalance;
    cashbackUrl: IWidgetPersonalAccountCashbackUrl;
    isCashbackAvailable?: boolean;
    isCashbackV2Enabled?: boolean;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    scores: string;
    translation: IWidgetPersonalAccountSuccessTranslation;
}

const WidgetPersonalAccountCashback: FC<IWidgetPersonalAccountCashbackProps> = props => {

    const {
        balance,
        cashbackUrl,
        isCashbackV2Enabled,
        pushGtmAnalytic,
        scores,
        translation,
    } = props;

    const isPositiveBalance: boolean = (balance.value ?? 0) > 0;
    const urlHowToSpentCashback: string = cashbackUrl.urlHowToSpentCashback ?? '';
    const urlHowToGetCashback: string = cashbackUrl.urlHowToGetCashback ?? '';

    const isCustomRoleCheckAvailable = useCheckCustomRole(true, AuthorityRoleEnum.ROLE_CASHBACK_MANAGE);

    const openCashbackLink = useCallback(() => {
        const url = isPositiveBalance ? urlHowToSpentCashback : urlHowToGetCashback;
        openExternalRoute(url, ELinkTarget.BLANK);
        // GTM Analytic
        pushGtmAnalytic({
            operation: GTMOperations.widgetPersonalAccount.clickLinkHowToGetCashbackWidgetPersonalAccount(
                location.origin + url,
            ),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    }, [isPositiveBalance, pushGtmAnalytic, urlHowToGetCashback, urlHowToSpentCashback]);

    const openCashbackControlLink = useCallback(() => {
        pushGtmAnalytic({
            operation:
                GTMOperations.widgetPersonalAccount.clickLinkCashbackControlWidgetPersonalAccount(
                    location.origin + RoutePath.cashback,
                ),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    }, [pushGtmAnalytic]);

    return (
        <FlexColumnStyled className="widget-personal-account__cashback">
            <FlexStyled className="mb-1">
                <WidgetPersonalAccountCashbackTitleStyled>
                    {translation.titleCashback}
                </WidgetPersonalAccountCashbackTitleStyled>
                <LayoutStyled className="ml-1" height="20px">
                    <ArrowTooltip
                        dataName="toolTipBalanceDescription"
                        placement="right"
                        title={balance.description}
                    >
                        <ImgStyled className="small" src={icons.infoBlueIcon}/>
                    </ArrowTooltip>
                </LayoutStyled>
            </FlexStyled>
            <FlexStyled alignItems="center">
                <WidgetPersonalAccountCashbackRowBodyStyled>
                    {formatBalanceWithoutTrailingZeros(balance.value || 0)}&nbsp;
                    {declination(balance.value || 0, scores)}
                </WidgetPersonalAccountCashbackRowBodyStyled>
                {isCustomRoleCheckAvailable && (
                    <LayoutStyled className="ml-1">
                        {
                            !isCashbackV2Enabled && (urlHowToSpentCashback || urlHowToGetCashback) &&
                            <LinkBlock
                                className="widget-personal-account__cashback-link"
                                dataName="openCashBack"
                                onClick={openCashbackLink}
                            >
                                {isPositiveBalance ? translation.linkCashback : translation.linkCashbackZero}
                            </LinkBlock>
                        }
                        {
                            isCashbackV2Enabled &&
                            <LinkBlock
                                className="widget-personal-account__cashback-link"
                                dataName="openCashBack"
                                url={RoutePath.cashback}
                                onClick={openCashbackControlLink}
                            >
                                {translation.linkCashbackControl}
                            </LinkBlock>
                        }
                    </LayoutStyled>)
                }
            </FlexStyled>
        </FlexColumnStyled>
    );
};

export default WidgetPersonalAccountCashback;
